import React from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { HeadingText50, BodyTextMedium, Icon } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';

import useShowSkeletonDelay from 'src/shared/hooks/useShowSkeletonDelay';
import NotFoundPage from 'src/modules/404/pages/NotFound';
import { mediaQueryMaxWidth } from 'src/shared/utils';
import { captureException } from 'src/shared/utils/captureException';

import { ReactComponent as Confirmation } from '../assets/images/Confirmation.svg';
import { ReactComponent as SocialGoogle } from '../assets/images/social_google.svg';
import { ReactComponent as SocialOutlook } from '../assets/images/social_outlook.svg';
import { useGetCalendarLinksQuery } from '../documents/topic.generated';
import TopicPageSkeleton from './skeletons/TopicPageSkeleton';

interface RsvpProps {
  path?: string;
  sessionId: string;
  rsvpId: string;
  topicSlug: string;
}

const PageWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: '53.5rem',
  margin: '0 auto',
  alignItems: 'center',
});

const StyledCalendarLinkContainer = styled.a({
  maxWidth: '264px',
  display: 'inline-flex',
  border: `2px solid ${colors.gray05}`,
  padding: '16px 26px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  '& svg': {
    maxWidth: 16,
    maxHeight: 16,
  },
});

const LinksContainer = styled.div({
  display: 'flex',
  gap: 16,
  marginTop: 16,
  [mediaQueryMaxWidth(550)]: {
    flexDirection: 'column',
  },
});

const InlineStyledCalendarLink = ({
  href,
  icon,
  text,
}: {
  text: string;
  href: string;
  icon: JSX.Element;
}) => {
  return (
    <StyledCalendarLinkContainer href={href} target="_blank" rel="noreferrer">
      <div css={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <BodyTextMedium css={{ margin: '0 12px 0 8px' }} fontColor="gray55">
          {text}
        </BodyTextMedium>
        <Icon size={20} color="gray55" name="chevron-right" />
      </div>
    </StyledCalendarLinkContainer>
  );
};

const RsvpEmailToCalendar: React.FC<unknown> = () => {
  const { rsvpId } = useParams<RsvpProps>();

  const { data: rsvpData, loading: rsvpLoading } = useGetCalendarLinksQuery({
    variables: {
      rsvpId,
    },
  });

  //TODO: Add session information once sessionUUID is available
  //TODO: Calendar confirmation Skeleton- Using TopicPageSkeleton for now
  const show = useShowSkeletonDelay(!!rsvpData);

  if (show || (rsvpLoading && !rsvpData)) {
    return <TopicPageSkeleton />;
  }

  if (rsvpData?.circleCalendarLinksByRsvp?.__typename !== 'CircleCalendarLinks') {
    const errMsg = `An unexpected typename was found: ${rsvpData?.circleCalendarLinksByRsvp?.__typename}`;
    Sentry.captureException(new Error(errMsg));
    captureException(new Error(errMsg));
    return <NotFoundPage />;
  }

  const googleLink = rsvpData.circleCalendarLinksByRsvp.google;
  const outlookLink = rsvpData.circleCalendarLinksByRsvp.outlook;
  const ics = rsvpData.circleCalendarLinksByRsvp.ics;

  return (
    <PageWrapper>
      <Confirmation height="15rem" width="15rem" css={{ marginBottom: '3rem' }} />
      <HeadingText50>You're in!</HeadingText50>
      <BodyTextMedium
        css={{ maxWidth: '396px', marginBottom: '16px', textAlign: 'center' }}
        fontColor="gray55"
      >
        Select your calendar provider below, or use .ics to import this event to the calendar app of
        your choice.
      </BodyTextMedium>
      <LinksContainer>
        {googleLink ? (
          <InlineStyledCalendarLink
            href={googleLink}
            icon={<SocialGoogle />}
            text="Google Calendar"
          />
        ) : null}
        {outlookLink ? (
          <InlineStyledCalendarLink
            href={outlookLink}
            icon={<SocialOutlook />}
            text="Microsoft Outlook"
          />
        ) : null}
        {ics ? (
          <InlineStyledCalendarLink
            href={`https://api.joinmodernhealth.com${ics}`}
            icon={<Icon color="gray55" name="calendar-date" />}
            text="Add to calendar (.ics)"
          />
        ) : null}
      </LinksContainer>
    </PageWrapper>
  );
};

export default RsvpEmailToCalendar;
